export const buildSetUserPropertiesEvent = (contactId, siteVersion) => ({
  event: 'set_user_properties',
  contactId,
  siteVersion
});

export const buildSetUserPolicyNumbersEvent = (policyNumbers) => ({
  event: 'set_user_policy_numbers',
  policyNumbers: policyNumbers.join(',')
});

export const buildDocumentDownloadEvent = (documentType, policyNumber) => ({
  event: 'document_downloaded',
  documentType,
  policyNumber
});

export const buildSelectPromotionEvent = (promotion) => ({
  event: 'promotion_selected',
  promotion
});

export const buildDocumentUploadEvent = (documentType, policyNumber) => ({
  event: 'document_uploaded',
  documentType,
  policyNumber
});

export const buildWhatsCoveredEvent = (policyNumber) => ({
  event: 'whats_covered_clicked',
  policyNumber
});

export const buildInteractionEvent = (eventType, payload = {}) => ({
  ...payload,
  event: 'element_interaction',
  eventType
});

// Answer bodies are CMS rich text, so their links can't carry React handlers. A dedicated
// event means GTM doesn't need a broad anchor-text trigger that also catches links outside
// the accordions. snake_case here so faq_question is one dimension shared with the renewal
// hub's faq_interaction event, rather than two registrations for the same concept.
export const buildFaqAnswerLinkClickEvent = ({ faqQuestion, linkText, linkUrl }) => ({
  event: 'faq_answer_link_click',
  faq_question: faqQuestion,
  link_text: linkText,
  link_url: linkUrl
});

export const buildTourStartedEvent = () => ({
  event: 'tour_started'
});

export const buildTourStepEvent = (stepIndex, stepTarget) => ({
  event: 'tour_step',
  tourStep: stepIndex + 1,
  // Targets are CSS selectors like '.petContainer'; strip the leading dot for readability.
  // Joyride also accepts element refs, hence the string guard.
  tourStepName: typeof stepTarget === 'string' ? stepTarget.replace(/^\./, '') : undefined
});

// Named for what it measures: a valid form submission, not a consultation that began.
// It fires before generateConsultationUrl, which can fail — so "started" would overcount.
export const buildVetConsultSubmittedEvent = (consultType) => ({
  event: 'vet_consult_submitted',
  consultType
});

// GA4 cannot express "explicitly unset", and sanitize() rebuilds objects via Object.entries, so an
// undefined value would otherwise reach the data layer as a present key. Keeps false and 0.
const withoutAbsentValues = (event) =>
  Object.fromEntries(Object.entries(event).filter(([, value]) => value !== undefined));

export const buildRenewalBannerImpressionEvent = ({
  policyNumber,
  bannerLocation,
  bannerVariant,
  bannerCtaLabel,
  canRenew,
  daysToExpiry
}) =>
  // banner_cta_label is absent on My Pets banners for a policy that cannot be renewed yet:
  // banners.js renders no button there.
  withoutAbsentValues({
    event: 'renewal_banner_impression',
    policy_number: policyNumber,
    banner_location: bannerLocation,
    banner_variant: bannerVariant,
    banner_cta_label: bannerCtaLabel,
    can_renew: canRenew,
    days_to_expiry: daysToExpiry
  });

export const buildRenewalBannerClickEvent = ({
  policyNumber,
  bannerLocation,
  bannerVariant,
  bannerCtaLabel,
  destinationPage
}) => ({
  event: 'renewal_banner_click',
  policy_number: policyNumber,
  banner_location: bannerLocation,
  banner_variant: bannerVariant,
  banner_cta_label: bannerCtaLabel,
  destination_page: destinationPage
});

export const buildRenewalHubEntryEvent = ({ policyNumber, entrySource, entrySourcePage }) => ({
  event: 'renewal_hub_entry',
  policy_number: policyNumber,
  entry_source: entrySource,
  entry_source_page: entrySourcePage
});

export const buildAdjustCoverUnavailableEvent = ({ policyNumber, reason }) => ({
  event: 'adjust_cover_unavailable',
  policy_number: policyNumber,
  reason
});

// element_type carries the help mechanism, not selected_value, so selected_value holds only the
// values a cover control resolved to. Help events pass neither value.
export const buildAdjustCoverInteractionEvent = ({
  policyNumber,
  elementType,
  fieldName,
  selectedValue,
  previousValue
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_interaction',
    policy_number: policyNumber,
    element_type: elementType,
    field_name: fieldName,
    selected_value: selectedValue,
    previous_value: previousValue
  });

export const buildAdjustCoverSubmitEvent = ({
  policyNumber,
  excess,
  benefitPercentage,
  routineCare,
  boosterCare,
  annualBenefitLimit,
  newPremium,
  newPremiumAnnual,
  premiumDirection,
  claimExampleBill,
  claimExampleWePay,
  isCoverAdjusted
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_submit',
    policy_number: policyNumber,
    excess,
    benefit_percentage: benefitPercentage,
    routine_care: routineCare,
    booster_care: boosterCare,
    annual_benefit_limit: annualBenefitLimit,
    new_premium: newPremium,
    new_premium_annual: newPremiumAnnual,
    premium_direction: premiumDirection,
    claim_example_bill: claimExampleBill,
    claim_example_we_pay: claimExampleWePay,
    is_cover_adjusted: isCoverAdjusted
  });

export const buildAdjustCoverSubmitResultEvent = ({ policyNumber, result }) => ({
  event: 'adjust_cover_submit_result',
  policy_number: policyNumber,
  result
});

// new_premium is per payment period, so it needs the frequency and the annualised figure to be
// comparable across customers.
export const buildAdjustCoverConfirmationViewEvent = ({
  policyNumber,
  excess,
  benefitPercentage,
  routineCare,
  boosterCare,
  annualBenefitLimit,
  newPremium,
  newPremiumAnnual,
  paymentFrequency
}) =>
  withoutAbsentValues({
    event: 'adjust_cover_confirmation_view',
    policy_number: policyNumber,
    excess,
    benefit_percentage: benefitPercentage,
    routine_care: routineCare,
    booster_care: boosterCare,
    annual_benefit_limit: annualBenefitLimit,
    new_premium: newPremium,
    new_premium_annual: newPremiumAnnual,
    payment_frequency: paymentFrequency
  });

export const buildFaqInteractionEvent = ({ policyNumber, faqQuestion, interactionType, pageName }) => ({
  event: 'faq_interaction',
  policy_number: policyNumber,
  faq_question: faqQuestion,
  interaction_type: interactionType,
  page_name: pageName
});
